import { faPencilAlt, faEye, faLightbulb, faFileDownload } from '@fortawesome/free-solid-svg-icons';
import * as React from 'react';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { ShopViewModel, UserRole } from 'stageclip-common';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import { RowInfo } from 'react-table';

import { createModalOpenAction } from '../../actions/modalActions';
import { FormId } from '../../constants/formId';
import * as PermissionsHelper from '../../helpers/permissionsHelper';
import { DownloadSummaryClient, NormalizedClient } from '../../models/normalized/normalizedClient';
import { ClippingPlatformState } from '../../store/clippingPlatformState';
import { ById } from '../../store/normalizedState';
import Button from '../buttons/button';
import {
  baseMapDispatchToProps,
  baseMapStateToProps,
  ComponentBase,
  BaseDispatchProps,
  BaseStateProps,
} from '../componentBase';
import { AppTable, AppTableColumn } from '../table/appTable';
import { EmailTemplateModal } from '../emailTemplate/emailTemplateModal';
import { SurveyTemplateModal } from '../surveyTemplate/surveyTemplateModal';

import { ActionLogModal } from 'components/actionLogModal/ActionLogModal';
import { ArchiveClientModal } from './archiveClientModal/ArchiveClientModal';
import { fetchClientActions } from 'api/clients/clients.actions';
import { SimpleModalId } from 'constants/modalId';
import { normalizeClient } from 'api/normalizers/clientNormalizers';
import { InternalActionTypes } from 'constants/actionTypes';
import { Response } from 'helpers/callApi/callApi.types';
import { EditClientForm } from './editClientForm';
import { formatTimezoneLabel } from 'helpers/dateHelpers';
import { DownloadSummaryForm } from './downloadSummaryForm';

interface ClientsTableStateProps extends BaseStateProps {
  clients?: NormalizedClient[];
  shops: ById<ShopViewModel>;
  allClientsLoaded?: boolean;
  archived?: boolean;
  downloadSummaryClient?: DownloadSummaryClient;
}

interface ClientsTableDispatchProps extends BaseDispatchProps {
  openEditClientForm: (c: NormalizedClient) => void;
  openEmailTemplatePreviewModal: (clientId: string) => void;
  openSurveyTemplatePreviewModal: (clientId: string) => void;
  updateClient: (payload: any) => void;
  openArchiveClientModal: (client: NormalizedClient) => void;
  getClientLogs: (clientId: string) => Response;
  openClientActionLog: (c: NormalizedClient) => void;
  openDownloadSummaryForm: (c: DownloadSummaryClient) => void;
}

class ClientsTable extends ComponentBase<RouteComponentProps, ClientsTableStateProps, ClientsTableDispatchProps> {
  private columns: AppTableColumn[] = [
    {
      Header: 'Name',

      accessor: (client: NormalizedClient) => client.name,
      id: 'name',
    },
    {
      Header: 'Reference',
      accessor: (client: NormalizedClient) => client.reference,
      id: 'reference',
    },
    {
      Header: 'Timezone',
      accessor: (client: NormalizedClient) => formatTimezoneLabel(client.timezone),
      id: 'timezone',
    },
    {
      Header: 'Shop',
      accessor: (client: NormalizedClient) => (client.shop ? this.props.shops[client.shop].name : 'n/a'),
      id: 'shop',
    },
    {
      show: this.props.currentUser.role !== UserRole.ReadOnly,
      Cell: (row: RowInfo) => {
        const {
          history,
          match,
          openEmailTemplatePreviewModal,
          openSurveyTemplatePreviewModal,
          openArchiveClientModal,
          archived,
          openClientActionLog,
        } = this.props;
        const { id, emailUpdated, surveyUpdated } = row.original as NormalizedClient;

        return (
          <>
            {PermissionsHelper.userCan(PermissionsHelper.RestrictedActions.EditClient) && (
              <Button
                text="Edit Client"
                onClick={this.getOpenEditClientFormCallback(row.original)}
                icon={faPencilAlt}
                allCapitals
                slimButton
              />
            )}

            <Button
              text="Users"
              onClick={() => history.push(`${match.url}/${id}`)}
              icon={faEye}
              allCapitals
              slimButton
            />

            <Button
              text="Map Clip Templates"
              onClick={() =>
                history.push(`${match.url}/${id}/clip-template-mapper`, {
                  clipConfiguration: row.original.clipConfiguration,
                })
              }
              icon={faEye}
              allCapitals
              slimButton
            />

            <Button
              text="Email Template"
              onClick={() => openEmailTemplatePreviewModal(id)}
              icon={faLightbulb}
              allCapitals
              slimButton
              inverseColours={!emailUpdated}
            />

            <Button
              text="Survey Templates"
              onClick={() => openSurveyTemplatePreviewModal(id)}
              icon={faLightbulb}
              allCapitals
              slimButton
              inverseColours={!surveyUpdated}
            />

            <Button
              text="Action logs"
              onClick={() => openClientActionLog(row.original)}
              icon={faEye}
              allCapitals
              slimButton
              inverseColours={!surveyUpdated}
            />

            <Button
              text="Summary Stats"
              onClick={this.getOpenDownloadSummaryFormCallback(row.original)}
              icon={faFileDownload}
              allCapitals
              slimButton
              inverseColours={!surveyUpdated}
            />

            {!archived && (
              <Button
                text="Archive"
                onClick={() => openArchiveClientModal(row.original as NormalizedClient)}
                icon={faEye}
                allCapitals
                slimButton
                inverseColours
              />
            )}
            {archived && (
              <Button
                text="Unarchive"
                onClick={() => openArchiveClientModal(row.original as NormalizedClient)}
                icon={faEye}
                allCapitals
                slimButton
                inverseColours
              />
            )}
          </>
        );
      },
      Header: 'Actions',
      id: 'actions',
      style: {
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
      },
    },
  ];

  protected dataLoaded(props: any) {
    return props.allClientsLoaded;
  }

  protected async loadData(props: any) {
    if (!props.allClientsLoaded) {
      await props.loadAllClients(this);
    }
  }

  protected loggedInRender() {
    return (
      <>
        <AppTable
          columns={this.columns}
          data={this.props.clients}
          tableName="clients"
          defaultSorted={[{ id: 'name', desc: false }]}
        />
        <EditClientForm />
        <DownloadSummaryForm />
        <EmailTemplateModal onClose={this.props.updateClient} />
        <SurveyTemplateModal onClose={this.props.updateClient} />
        <ArchiveClientModal />
        <ActionLogModal getData={this.props.getClientLogs} type="Client" />
      </>
    );
  }

  private getOpenEditClientFormCallback(client: NormalizedClient) {
    const callback = () => this.props.openEditClientForm(client);

    return callback.bind(this);
  }

  private getOpenDownloadSummaryFormCallback(client: DownloadSummaryClient) {
    const callback = () => this.props.openDownloadSummaryForm(client);

    return callback.bind(this);
  }
}

const mapStateToProps = (state: ClippingPlatformState): ClientsTableStateProps => {
  return {
    ...baseMapStateToProps(state),
    shops: state.entities.shops.byId,
  };
};

const getFormPropsFromClient = (client: NormalizedClient) => ({
  clientId: client.id,
  clipConfiguration: JSON.parse(client.clipConfiguration),
  domain: client.domain,
  colors: client.colors,
  emails: client.emails || false,
  googleAnalyticsId: client.googleAnalyticsId,
  youTubePlaylistId: client.youTubePlaylistId,
  name: client.name,
  shopId: client.shop,
  survey: client.survey || false,
  clipTemplateEditor: client.clipTemplateEditor || false,
  dst: client.dst || false,
  studentsAmount: client.studentsAmount,
  timezone: client.timezone,
  eventMonths: client.eventMonths,
  logo: client.logo,
});

const getOpenDownloadSummaryFormCallback = (client: DownloadSummaryClient) => ({
  clientId: client.id,
  name: client.name,
});

const mapDispatchToProps = (dispatch: Dispatch): ClientsTableDispatchProps => {
  return {
    ...baseMapDispatchToProps(dispatch),
    openEditClientForm: c => dispatch(createModalOpenAction(FormId.EditClientForm, getFormPropsFromClient(c))),
    openEmailTemplatePreviewModal: clientId =>
      dispatch(createModalOpenAction('email-template-modal' as any, { clientId })),
    openSurveyTemplatePreviewModal: clientId =>
      dispatch(createModalOpenAction('survey-template-modal' as any, { clientId })),
    updateClient: payload => dispatch({ type: InternalActionTypes.ClientEdited, result: normalizeClient(payload) }),
    openArchiveClientModal: client => dispatch(createModalOpenAction(SimpleModalId.ArchiveClient, { client })),
    // @ts-ignore
    getClientLogs: clientId => dispatch(fetchClientActions(clientId)),
    openClientActionLog: client =>
      dispatch(
        createModalOpenAction(SimpleModalId.ActionLog, {
          fetchDataId: client.id,
          name: client.name,
        }),
      ),
    openDownloadSummaryForm: c =>
      dispatch(createModalOpenAction(FormId.DownloadSummaryForm, getOpenDownloadSummaryFormCallback(c))),
  };
};

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(ClientsTable));
